import { useState, useEffect } from "react";
import "./Login.scss";
import Auth_Logo from "../../assets/images/logo/logo.svg";
import Eye from "../../assets/images/vactor/eye.svg";
import EyeLine from "../../assets/images/vactor/eye-line.svg";
import Right_Arrow from "../../assets/images/vactor/arrow-right.svg";
import { IconButton, InputAdornment, TextField } from "@mui/material";
import { PrimaryButton } from "../../components/AllButtons/AllButtons";
import { Link, useNavigate } from "react-router-dom";
import { loginUser } from "../../Services/auth.service";
import { useAppDispatch } from "../../Store/store";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { openSnackbar } from "../../Slice/snackbarSlice";
import TitleComponent from "../../components/CommonElements/TitleComponent/TitleComponent";
import { login } from "../../Slice/userSlice";
import { getUserDetails } from "../../Services/profile.service";

const Login = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [fieldTouched, setFieldTouched] = useState({
    username: false,
    password: false
  });

  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'auth-token' && e.newValue) {
        const role = localStorage.getItem("role");
        if (role === "admin") {
          navigate("/admin");
        } else {
          navigate("/dashboard");
        }
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [navigate]);

  const validationSchema = Yup.object({
    username: Yup.string()
      .matches(/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/, "Invalid email format")
      .required("Email is required"),
    password: Yup.string().required("Password is required"),
  });

  const handleSubmit = async (values: { username: string; password: string }) => {
    const token = localStorage.getItem("auth-token");
    const role = localStorage.getItem("role");
    if (token) {
      if (role === "admin") {
        navigate("/admin");
      } else {
        navigate("/dashboard");
      }
      return;
    }
    try {
      const response = await dispatch(loginUser(values)).unwrap();
      if (response.status) {
        dispatch(openSnackbar({ message: response.message + " 🎉", severity: "success" }));
        dispatch(login(response));
        localStorage.removeItem("tour");
        dispatch(getUserDetails(response.data.id));
        if (response.data.role === "admin") {
          navigate("/admin");
        } else {
          navigate("/dashboard");
        }
      } else {
        dispatch(openSnackbar({ message: response.message || "Invalid Credentials ❌", severity: "error" }));
      }
    } catch (error) {
      dispatch(openSnackbar({ message: "Login failed ❌", severity: "error" }));
    }
  };

  return (
    <div className="auth-wrapper">
      <div className="row mx-0">
        <TitleComponent />
        <div className="col-md-12 col-lg-5 col-xl-4 p-0">
          <div className="auth-form-block">
            <div className="auth-form-content-block">
              <div className="auth-logo">
                <img src={Auth_Logo} alt="Logo" />
              </div>
              <p className="content-dsc-text">Welcome Back!</p>
              <Formik
                initialValues={{ username: "", password: "" }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                validateOnChange={true}
                validateOnBlur={false}
              >
                {({ errors, values, handleChange, isValid }) => {
                  // Check if both fields have values to determine if button should be enabled
                  const buttonShouldBeEnabled = values.username.trim() !== "" &&
                    values.password.trim() !== "" &&
                    isValid;

                  return (
                    <Form className="w-100">
                      <div className="input-form-field-wrapper">
                        <TextField
                          className="out-label-input-text"
                          id="username"
                          name="username"
                          placeholder="Email address"
                          margin="normal"
                          variant="outlined"
                          fullWidth
                          type="email"
                          value={values.username}
                          onChange={(e) => {
                            handleChange(e);
                            setFieldTouched(prev => ({ ...prev, username: true }));
                          }}
                          error={fieldTouched.username && Boolean(errors.username)}
                          helperText={fieldTouched.username && errors.username}
                        />
                      </div>

                      <div className="input-form-field-wrapper">
                        <TextField
                          className="out-label-input-text"
                          name="password"
                          placeholder="Password"
                          margin="normal"
                          variant="outlined"
                          type={showPassword ? "text" : "password"}
                          fullWidth
                          value={values.password}
                          onChange={(e) => {
                            handleChange(e);
                            setFieldTouched(prev => ({ ...prev, password: true }));
                          }}
                          error={fieldTouched.password && Boolean(errors.password)}
                          helperText={fieldTouched.password && errors.password}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle password visibility"
                                  edge="end"
                                  onClick={() => setShowPassword(!showPassword)}
                                  disabled={values.password.length === 0}
                                >
                                  <img src={showPassword ? Eye : EyeLine} alt="Toggle password" />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </div>

                      <div className="forgot-link" style={{ marginTop: "1px" }}>
                        <Link to="/forgot_password">Forgot password?</Link>
                      </div>

                      <div className="auth-action-control">
                        <PrimaryButton
                          type="submit"
                          disabled={!buttonShouldBeEnabled}
                          style={{
                            backgroundColor: buttonShouldBeEnabled ? "" : "",
                            color: "#fff",
                            cursor: buttonShouldBeEnabled ? "pointer" : "not-allowed",
                          }}
                        >
                          Sign in
                          <img src={Right_Arrow} alt="Right_Arrow" />
                        </PrimaryButton>
                      </div>

                      <div className="auth-info-text">
                        Don't have an account?{" "}
                        <Link className="auth-link" to="/">
                          Sign up
                        </Link>
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;